import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Card, CardMedia, CardContent, Box, Typography, } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useConversationStore } from '../../ConversationStoreProvider';
import { replaceStylesheetGlobals } from '../../portal/theme/portalTheme';
import Image from '../Image';
import ItemTitle from '../ItemTitle';
const formatPrice = (price) => `$${price.toFixed(2)}`;
const LegacyProductCard = ({ title, image, url, width, customStyle, productFormatting, price, description, showAddToCart = false, variantIds = [], isSingleVariant = false, isMultiVariant = false, onProductClick, product, }) => {
    const conversationStore = useConversationStore();
    const [selectedVariantId, setSelectedVariantId] = useState(isSingleVariant ? variantIds[0] : null);
    const [showVariants, setShowVariants] = useState(false);
    const resolvedProductFormatting = useMemo(() => ({
        template: 'custom',
        orientation: 'vertical',
        showBox: true,
        showImage: true,
        showTitle: true,
        showDescription: false,
        showPrice: false,
        backgroundColor: '#fff',
        ...productFormatting,
        ...customStyle,
        borderStyle: {
            borderWidth: 1,
            borderColor: '#ccc',
            borderRadius: 4,
            ...(productFormatting?.borderStyle || {}),
        },
    }), [productFormatting, customStyle]);
    const { orientation, showBox, showImage, showTitle, showDescription, showPrice, borderStyle, backgroundColor, } = resolvedProductFormatting;
    const isHorizontal = orientation === 'horizontal';
    const maxCardWidth = isHorizontal ? '600px' : '300px';
    useEffect(() => {
        conversationStore.currentConversation?.sendTrackingEvent('product_view', {
            product_name: title,
            product_url: url,
        });
    }, [title, url, conversationStore.currentConversation]);
    const handleClick = (e) => {
        e.preventDefault();
        // Send tracking event
        conversationStore.currentConversation?.sendTrackingEvent('product_click', {
            product_name: title,
            product_url: url,
        });
        // Use onProductClick if available, otherwise use default link behavior
        console.log('[LegacyProductCard] Click handler called, onProductClick:', !!onProductClick, 'product:', product);
        if (onProductClick && product) {
            console.log('[LegacyProductCard] Calling onProductClick with:', product);
            onProductClick(product);
        }
        else {
            console.log('[LegacyProductCard] Using default link behavior');
            const newWindow = window.open(url, '_top');
            if (newWindow) {
                newWindow.opener = null;
            }
        }
    };
    // Determine if we should show links and enable click
    const shouldShowLinks = productFormatting?.showLinks !== false;
    const shouldEnableClick = productFormatting?.enableClick !== false;
    const cardContent = (_jsxs(Card, { sx: {
            ...customStyle,
            width: width || '100%',
            maxWidth: maxCardWidth,
            display: 'grid',
            gridTemplateColumns: {
                xs: '1fr',
                md: isHorizontal && showImage ? '200px auto' : '1fr',
            },
            gap: isHorizontal ? 0 : 2,
            overflow: 'hidden',
            boxShadow: showBox
                ? '0px 2px 8px rgba(0, 0, 0, 0.1)'
                : 'none',
            border: showBox
                ? (theme) => `${borderStyle.borderWidth}px solid ${replaceStylesheetGlobals(theme, borderStyle.borderColor) || borderStyle.borderColor}`
                : 'none',
            borderRadius: borderStyle.borderRadius,
            backgroundColor,
            position: 'relative',
        }, children: [showAddToCart && (_jsx("button", { type: "button", onClick: (e) => {
                    e.preventDefault();
                    if (isSingleVariant && selectedVariantId) {
                        // Single variant - add directly to cart
                        window.parent.postMessage({
                            source: 'microcommerce-ai-embed',
                            type: 'addToCart',
                            details: {
                                variantId: selectedVariantId,
                                quantity: 1,
                            },
                        }, '*');
                    }
                    else if (isMultiVariant) {
                        // Multiple variants - toggle variant selection
                        setShowVariants(!showVariants);
                    }
                }, style: {
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#1976d2',
                    zIndex: 2,
                }, "aria-label": isMultiVariant ? 'Select variant' : 'Add to cart', children: _jsx(ShoppingCartIcon, {}) })), showAddToCart && isMultiVariant && showVariants && (_jsxs(Box, { sx: {
                    position: 'absolute',
                    top: 50,
                    right: 8,
                    background: 'white',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    padding: 2,
                    zIndex: 3,
                    minWidth: 200,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }, children: [_jsx(Typography, { variant: "body2", sx: { mb: 1, fontWeight: 'bold' }, children: "Select from the following options:" }), variantIds.map((variantId) => (_jsxs("button", { type: "button", onClick: (e) => {
                            e.preventDefault();
                            setSelectedVariantId(variantId);
                            setShowVariants(false);
                            window.parent.postMessage({
                                source: 'microcommerce-ai-embed',
                                type: 'addToCart',
                                details: {
                                    variantId,
                                    quantity: 1,
                                },
                            }, '*');
                        }, style: {
                            display: 'block',
                            width: '100%',
                            padding: '8px 12px',
                            margin: '4px 0',
                            background: selectedVariantId === variantId ? '#1976d2' : '#f5f5f5',
                            color: selectedVariantId === variantId ? 'white' : '#333',
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            cursor: 'pointer',
                            textAlign: 'left',
                        }, children: ["Variant", ' ', variantId] }, variantId)))] })), showImage && (_jsx(CardMedia, { sx: {
                    width: '100%',
                    maxWidth: isHorizontal ? '95%' : undefined,
                    height: isHorizontal ? '100%' : '200px',
                    alignSelf: 'stretch',
                    justifySelf: 'start',
                }, children: _jsx(Image, { url: image, status: "loaded", alt: title, customStyle: {
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        alignSelf: 'stretch',
                        ...(isHorizontal && {
                            borderTopLeftRadius: borderStyle.borderRadius,
                            borderBottomLeftRadius: borderStyle.borderRadius,
                        }),
                    } }) })), isHorizontal ? (_jsxs(CardContent, { sx: {
                    p: 2,
                    pl: isHorizontal ? 0 : 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    '&:last-child': { pb: 2 },
                }, children: [showTitle && (_jsx(Typography, { variant: "h6", sx: { fontWeight: 'bold' }, children: title })), showPrice && price && (_jsx(Box, { sx: { mt: 1 }, children: _jsx(Typography, { variant: "h5", children: price }) })), showDescription && description && (_jsx(Typography, { variant: "body2", sx: { mt: 2 }, children: description }))] })) : ((showTitle || showDescription || showPrice) && (_jsxs(CardContent, { sx: {
                    p: 1,
                    display: 'block',
                    '&:last-child': { pb: 1 },
                }, children: [showTitle && _jsx(ItemTitle, { title: title }), showPrice && price && (_jsx(Typography, { variant: "h5", children: formatPrice(price) })), showDescription && description && (_jsx(Typography, { variant: "body2", sx: { mt: 2 }, children: description }))] })))] }));
    // Render with or without link wrapper based on productFormatting
    if (shouldShowLinks && shouldEnableClick) {
        return (_jsx("a", { href: url, onClick: handleClick, target: "_top", rel: "noreferrer", style: { textDecoration: 'none', color: 'inherit', position: 'relative' }, children: cardContent }));
    }
    if (shouldEnableClick && !shouldShowLinks) {
        // Click enabled but no link - use div with click handler
        return (_jsx("div", { onClick: handleClick, onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick(e);
                }
            }, role: "button", tabIndex: 0, style: { cursor: 'pointer', position: 'relative' }, children: cardContent }));
    }
    // No click, no link - just the card
    return cardContent;
};
LegacyProductCard.defaultProps = {
    customStyle: undefined,
    width: '',
    productFormatting: undefined,
    price: '',
    description: '',
    showAddToCart: false,
    variantIds: [],
    isSingleVariant: false,
    isMultiVariant: false,
    onProductClick: undefined,
    product: undefined,
};
export default LegacyProductCard;
//# sourceMappingURL=LegacyProductCard.js.map